import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UploadCloudIcon, XIcon, FileTextIcon, Loader2Icon, CheckCircle2Icon, AlertCircleIcon } from 'lucide-react';
import { PARTIES, ResponseStatus } from '../data';
interface UploadIntakeProps {
  onBack: () => void;
}
type Stage = 'config' | 'loading' | 'review';
interface ExtractedCard {
  id: string;
  number: string;
  question: string;
  response: string;
  objections: string;
  confidence: 'high' | 'amber';
  flags: ResponseStatus[];
}
const SAMPLE_EXTRACTED_DATA: ExtractedCard[] = [{
  id: '1',
  number: 'Form Interrogatory 2.1',
  question: 'State your full legal name, any former names, and your principal place of business.',
  response: 'Westfield Distribution LLC, a Delaware limited liability company with its principal place of business at 1420 Harbor Boulevard, Suite 300, Long Beach, California 90802. No former names.',
  objections: '',
  confidence: 'high',
  flags: ['Substantive']
}, {
  id: '2',
  number: 'Form Interrogatory 6.1',
  question: 'Identify all documents that support your denials in your answer to the complaint.',
  response: 'See documents produced in Production Set No. 1, Bates Nos. WDL-000001 through WDL-000847.',
  objections: 'Responding party objects to this interrogatory as overly broad and unduly burdensome to the extent it seeks identification of every document supporting every denial.',
  confidence: 'amber',
  flags: ['References Documents']
}, {
  id: '3',
  number: 'Form Interrogatory 8.7',
  question: 'State the total income you have lost as a result of the incident described in the complaint, and the method of calculation you used to arrive at that figure.',
  response: 'Responding party has not yet completed its calculation of damages. Discovery and investigation are ongoing.',
  objections: 'Responding party objects to this interrogatory as premature and vague.',
  confidence: 'high',
  flags: ['Objection Only']
}, {
  id: '4',
  number: 'Special Interrogatory No. 1',
  question: 'Describe in detail all quality control testing performed on the Model X-400 unit between January 2023 and June 2023.',
  response: 'Quality control testing on the X-400 unit during the specified period included standard batch testing per ISO 9001 protocols, monthly visual inspections, and quarterly stress testing. Records of these tests were maintained by the QC department under the supervision of Director of Quality Assurance Michael Torres.',
  objections: '',
  confidence: 'high',
  flags: ['Substantive']
}, {
  id: '5',
  number: 'Special Interrogatory No. 2',
  question: 'Identify every employee who participated in or had knowledge of discussions regarding a potential recall of the X-400 unit.',
  response: 'Michael Torres, Director of QA; Linda Park, VP of Operations; James Whitfield, General Counsel.',
  objections: 'Responding party objects to this interrogatory as overbroad and as seeking information protected by the attorney-client privilege to the extent it encompasses communications with counsel regarding potential recall. Without waiving said objections, Responding Party identifies the following individuals.',
  confidence: 'high',
  flags: ['Substantive']
}, {
  id: '6',
  number: 'Special Interrogatory No. 3',
  question: 'State the date on which you first received notice of any complaint, claim, or lawsuit relating to the X-400 unit.',
  response: 'Responding party received its first written complaint regarding the X-400 unit on or about September 3, 2023, from a retail customer in Orange County, California.',
  objections: '',
  confidence: 'high',
  flags: ['Substantive']
}, {
  id: '7',
  number: 'Special Interrogatory No. 4',
  question: 'Describe all modifications or design changes made to the X-400 unit after the date identified in your response to Special Interrogatory No. 3.',
  response: 'Following receipt of the initial complaint, the engineering team conducted a review and implemented a revised gasket seal design (Part No. GS-4401-R2) effective November 2023. See engineering change order ECO-2023-089, produced at Bates No. WDL-001204.',
  objections: 'Responding party objects to this interrogatory to the extent it calls for disclosure of proprietary engineering processes and trade secrets.',
  confidence: 'amber',
  flags: ['Substantive', 'References Documents']
}, {
  id: '8',
  number: 'Special Interrogatory No. 5',
  question: 'State the total number of X-400 units sold or distributed between January 2023 and December 2023.',
  response: 'Approximately 12,800 units.',
  objections: '',
  confidence: 'high',
  flags: ['Substantive']
}];
const ALL_FLAGS: ResponseStatus[] = ['Substantive', 'Objection Only', 'References Documents', 'Amended', 'Not Yet Received'];
export function UploadIntake({
  onBack
}: UploadIntakeProps) {
  const [stage, setStage] = useState<Stage>('config');
  // Form state
  const [party, setParty] = useState(PARTIES[1]);
  const [setNumber, setSetNumber] = useState('Set 3');
  const [responseType, setResponseType] = useState<'original' | 'amended'>('original');
  const [interrogatoryType, setInterrogatoryType] = useState<'form' | 'special'>('form');
  const [dateReceived, setDateReceived] = useState(new Date().toISOString().split('T')[0]);
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Review state
  const [cards, setCards] = useState<ExtractedCard[]>(SAMPLE_EXTRACTED_DATA);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  };
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };
  const handleProcess = () => {
    setStage('loading');
    setTimeout(() => {
      setStage('review');
    }, 1500);
  };
  const handleConfirm = () => {
    setShowSuccessToast(true);
    setTimeout(() => {
      onBack();
    }, 1500);
  };
  const updateCard = (id: string, field: keyof ExtractedCard, value: any) => {
    setCards(cards.map(c => c.id === id ? {
      ...c,
      [field]: value
    } : c));
  };
  const toggleFlag = (id: string, flag: ResponseStatus) => {
    setCards(cards.map(c => {
      if (c.id === id) {
        const newFlags = c.flags.includes(flag) ? c.flags.filter(f => f !== flag) : [...c.flags, flag];
        return {
          ...c,
          flags: newFlags
        };
      }
      return c;
    }));
  };
  const isFormValid = party && setNumber && dateReceived;
  return <div className="flex flex-col h-full bg-geniac-bg overflow-hidden relative" data-id="element-223">
      {/* Top Bar */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-geniac-border flex items-center justify-between" data-id="element-224">
        <button onClick={onBack} className="text-sm text-geniac-muted hover:text-geniac-text transition-colors" data-id="element-225">
          Cancel Upload
        </button>
      </div>

      <div className="flex-1 overflow-y-auto" data-id="element-226">
        <AnimatePresence mode="wait" data-id="element-227">
          {/* STAGE 1: CONFIG */}
          {stage === 'config' && <motion.div key="config" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} className="max-w-2xl mx-auto py-12 px-6" data-id="element-228">
              <h1 className="text-xl font-semibold text-geniac-text mb-8" data-id="element-229">
                Add Interrogatory Responses
              </h1>

              <div className="space-y-6" data-id="element-230">
                {/* Case */}
                <div data-id="element-231">
                  <label className="block text-xs font-semibold uppercase tracking-wider text-geniac-muted mb-2" data-id="element-232">
                    Case
                  </label>
                  <select className="w-full bg-geniac-panel border border-geniac-border rounded-lg px-3 py-2.5 text-sm text-geniac-text focus:outline-none focus:ring-1 focus:ring-geniac-accent" data-id="element-233">
                    <option data-id="element-234">
                      Martinez v. Acme Corp et al. — Case No. 2023-CV-04817
                    </option>
                  </select>
                </div>

                {/* Party & Set */}
                <div className="grid grid-cols-3 gap-4" data-id="element-235">
                  <div className="col-span-2" data-id="element-236">
                    <label className="block text-xs font-semibold uppercase tracking-wider text-geniac-muted mb-2" data-id="element-237">
                      Responding Party
                    </label>
                    <select value={party} onChange={e => setParty(e.target.value)} className="w-full bg-geniac-panel border border-geniac-border rounded-lg px-3 py-2.5 text-sm text-geniac-text focus:outline-none focus:ring-1 focus:ring-geniac-accent" data-id="element-238">
                      {PARTIES.map(p => <option key={p} value={p} data-id="element-239">
                          {p}
                        </option>)}
                      <option value="new" className="text-geniac-accent font-medium" data-id="element-240">
                        + Add new party
                      </option>
                    </select>
                  </div>
                  <div data-id="element-241">
                    <label className="block text-xs font-semibold uppercase tracking-wider text-geniac-muted mb-2" data-id="element-242">
                      Set Number
                    </label>
                    <input type="text" value={setNumber} onChange={e => setSetNumber(e.target.value)} className="w-full bg-geniac-panel border border-geniac-border rounded-lg px-3 py-2.5 text-sm text-geniac-text focus:outline-none focus:ring-1 focus:ring-geniac-accent" data-id="element-243" />
                  </div>
                </div>

                {/* Response Type */}
                <div data-id="element-244">
                  <label className="block text-xs font-semibold uppercase tracking-wider text-geniac-muted mb-2" data-id="element-245">
                    Response Type
                  </label>
                  <div className="flex gap-3" data-id="element-246">
                    <button onClick={() => setResponseType('original')} className={`flex-1 py-2.5 px-4 rounded-lg border text-sm font-medium transition-colors ${responseType === 'original' ? 'bg-geniac-accent/15 border-geniac-accent text-geniac-accent' : 'bg-geniac-bg border-geniac-border text-geniac-muted hover:text-geniac-text'}`} data-id="element-247">
                      Original Response
                    </button>
                    <button onClick={() => setResponseType('amended')} className={`flex-1 py-2.5 px-4 rounded-lg border text-sm font-medium transition-colors ${responseType === 'amended' ? 'bg-geniac-accent/15 border-geniac-accent text-geniac-accent' : 'bg-geniac-bg border-geniac-border text-geniac-muted hover:text-geniac-text'}`} data-id="element-248">
                      Amended Response
                    </button>
                  </div>
                  {responseType === 'amended' && <div className="mt-3" data-id="element-249">
                      <select className="w-full bg-geniac-panel border border-geniac-border rounded-lg px-3 py-2 text-sm text-geniac-text focus:outline-none focus:ring-1 focus:ring-geniac-accent" data-id="element-250">
                        <option data-id="element-251">
                          Amends: Set 1 responses received January 12, 2025
                        </option>
                        <option data-id="element-252">
                          Amends: Set 2 responses received March 28, 2025
                        </option>
                      </select>
                    </div>}
                </div>

                {/* Interrogatory Type & Date */}
                <div className="grid grid-cols-2 gap-4" data-id="element-253">
                  <div data-id="element-254">
                    <label className="block text-xs font-semibold uppercase tracking-wider text-geniac-muted mb-2" data-id="element-255">
                      Interrogatory Type
                    </label>
                    <div className="flex gap-2" data-id="element-256">
                      <button onClick={() => setInterrogatoryType('form')} className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${interrogatoryType === 'form' ? 'bg-geniac-accent/15 border-geniac-accent text-geniac-accent' : 'bg-geniac-bg border-geniac-border text-geniac-muted hover:text-geniac-text'}`} data-id="element-257">
                        Form
                      </button>
                      <button onClick={() => setInterrogatoryType('special')} className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${interrogatoryType === 'special' ? 'bg-geniac-accent/15 border-geniac-accent text-geniac-accent' : 'bg-geniac-bg border-geniac-border text-geniac-muted hover:text-geniac-text'}`} data-id="element-258">
                        Special
                      </button>
                    </div>
                  </div>
                  <div data-id="element-259">
                    <label className="block text-xs font-semibold uppercase tracking-wider text-geniac-muted mb-2" data-id="element-260">
                      Date Received
                    </label>
                    <input type="date" value={dateReceived} onChange={e => setDateReceived(e.target.value)} className="w-full bg-geniac-panel border border-geniac-border rounded-lg px-3 py-2 text-sm text-geniac-text focus:outline-none focus:ring-1 focus:ring-geniac-accent [color-scheme:dark]" data-id="element-261" />
                  </div>
                </div>

                {/* Drag and Drop Zone */}
                <div className="pt-4" data-id="element-262">
                  <input type="file" ref={fileInputRef} onChange={handleFileSelect} accept=".pdf" className="hidden" data-id="element-263" />

                  {!file ? <div onDragOver={e => e.preventDefault()} onDrop={handleFileDrop} onClick={() => fileInputRef.current?.click()} className="border-2 border-dashed border-geniac-border hover:border-geniac-accent/50 rounded-xl p-10 flex flex-col items-center justify-center cursor-pointer transition-colors bg-geniac-panel/30" data-id="element-264">
                      <UploadCloudIcon className="w-10 h-10 text-geniac-muted mb-4" data-id="element-265" />
                      <p className="text-sm text-geniac-text font-medium" data-id="element-266">
                        Drag and drop response PDF here, or click to browse
                      </p>
                      <p className="text-xs text-geniac-muted mt-2" data-id="element-267">
                        Supports PDF up to 50MB
                      </p>
                    </div> : <div className="border border-geniac-border rounded-xl p-4 flex items-center justify-between bg-geniac-panel" data-id="element-268">
                      <div className="flex items-center gap-3" data-id="element-269">
                        <div className="p-2 bg-geniac-bg rounded-lg border border-geniac-border" data-id="element-270">
                          <FileTextIcon className="w-6 h-6 text-geniac-accent" data-id="element-271" />
                        </div>
                        <div data-id="element-272">
                          <p className="text-sm font-medium text-geniac-text" data-id="element-273">
                            {file.name}
                          </p>
                          <p className="text-xs text-geniac-muted" data-id="element-274">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <button onClick={() => setFile(null)} className="p-2 text-geniac-muted hover:text-geniac-text hover:bg-geniac-hover rounded-md transition-colors" data-id="element-275">
                        <XIcon className="w-5 h-5" data-id="element-276" />
                      </button>
                    </div>}
                </div>

                {/* Submit */}
                <div className="pt-6" data-id="element-277">
                  <button onClick={handleProcess} disabled={!isFormValid} className="w-full py-3 rounded-lg bg-geniac-accent text-geniac-bg font-semibold text-sm transition-all hover:bg-geniac-accent/90 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-278">
                    Process Document
                  </button>
                </div>
              </div>
            </motion.div>}

          {/* STAGE 2: LOADING */}
          {stage === 'loading' && <motion.div key="loading" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="flex flex-col items-center justify-center h-full" data-id="element-279">
              <Loader2Icon className="w-10 h-10 text-geniac-accent animate-spin mb-4" data-id="element-280" />
              <p className="text-geniac-text font-medium" data-id="element-281">
                Extracting interrogatory responses...
              </p>
              <p className="text-sm text-geniac-muted mt-2" data-id="element-282">
                This usually takes a few seconds.
              </p>
            </motion.div>}

          {/* STAGE 3: REVIEW */}
          {stage === 'review' && <motion.div key="review" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="max-w-4xl mx-auto py-10 px-6 pb-32" data-id="element-283">
              <div className="mb-8" data-id="element-284">
                <h1 className="text-xl font-semibold text-geniac-text" data-id="element-285">
                  Review Extracted Responses
                </h1>
                <p className="text-sm text-geniac-muted mt-1" data-id="element-286">
                  We found {cards.length} interrogatory responses in this
                  document. Please review and correct any errors before
                  confirming.
                </p>
              </div>

              <div className="space-y-6" data-id="element-287">
                {cards.map(card => <div key={card.id} className="bg-geniac-panel border border-geniac-border rounded-lg overflow-hidden" data-id="element-288">
                    {/* Card Header */}
                    <div className="px-5 py-3 border-b border-geniac-border flex items-center justify-between bg-geniac-bg/50" data-id="element-289">
                      <input type="text" value={card.number} onChange={e => updateCard(card.id, 'number', e.target.value)} className="text-sm font-semibold text-geniac-text bg-transparent border-b border-transparent focus:border-geniac-accent focus:outline-none px-1 py-0.5 w-64" data-id="element-290" />
                      <div className="flex items-center gap-2 group relative" data-id="element-291">
                        <div className={`w-2.5 h-2.5 rounded-full ${card.confidence === 'high' ? 'bg-emerald-400' : 'bg-amber-400'}`} data-id="element-292" />
                        {/* Simple tooltip */}
                        <div className="absolute right-0 top-full mt-2 w-max px-2 py-1 bg-geniac-border text-geniac-text text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10" data-id="element-293">
                          {card.confidence === 'high' ? 'High confidence' : 'Review recommended'}
                        </div>
                      </div>
                    </div>

                    {/* Card Body */}
                    <div className="p-5 space-y-4" data-id="element-294">
                      <div data-id="element-295">
                        <label className="block text-xs font-semibold uppercase tracking-wider text-geniac-muted mb-1.5" data-id="element-296">
                          Question
                        </label>
                        <textarea value={card.question} onChange={e => updateCard(card.id, 'question', e.target.value)} rows={2} className="w-full bg-geniac-bg border border-geniac-border rounded-md px-3 py-2 text-sm text-geniac-text focus:outline-none focus:ring-1 focus:ring-geniac-accent resize-y" data-id="element-297" />
                      </div>

                      <div data-id="element-298">
                        <label className="block text-xs font-semibold uppercase tracking-wider text-geniac-muted mb-1.5" data-id="element-299">
                          Response
                        </label>
                        <textarea value={card.response} onChange={e => updateCard(card.id, 'response', e.target.value)} rows={4} className="w-full bg-geniac-bg border border-geniac-border rounded-md px-3 py-2 text-sm text-geniac-text focus:outline-none focus:ring-1 focus:ring-geniac-accent resize-y" data-id="element-300" />
                      </div>

                      <div data-id="element-301">
                        <label className="block text-xs font-semibold uppercase tracking-wider text-geniac-muted mb-1.5" data-id="element-302">
                          Objections
                        </label>
                        <textarea value={card.objections} onChange={e => updateCard(card.id, 'objections', e.target.value)} placeholder="No objections detected." rows={2} className="w-full bg-geniac-bg border border-geniac-border rounded-md px-3 py-2 text-sm text-geniac-text placeholder-geniac-muted focus:outline-none focus:ring-1 focus:ring-geniac-accent resize-y" data-id="element-303" />
                      </div>
                    </div>

                    {/* Card Footer - Flags */}
                    <div className="px-5 py-3 border-t border-geniac-border bg-geniac-bg/50 flex flex-wrap gap-2" data-id="element-304">
                      {ALL_FLAGS.map(flag => {
                  const isActive = card.flags.includes(flag);
                  // Use the same badge colors from DataTable when active
                  const activeStyles: Record<ResponseStatus, string> = {
                    Substantive: 'bg-emerald-900/30 text-emerald-400 border-emerald-800/50',
                    'Objection Only': 'bg-rose-900/30 text-rose-400 border-rose-800/50',
                    'References Documents': 'bg-blue-900/30 text-blue-400 border-blue-800/50',
                    Amended: 'bg-amber-900/30 text-amber-400 border-amber-800/50',
                    'Not Yet Received': 'bg-slate-800/50 text-slate-400 border-slate-700/50'
                  };
                  return <button key={flag} onClick={() => toggleFlag(card.id, flag)} className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${isActive ? activeStyles[flag] : 'bg-geniac-bg border-geniac-border text-geniac-muted hover:text-geniac-text hover:border-geniac-muted/50'}`} data-id="element-305">
                            {flag}
                          </button>;
                })}
                    </div>
                  </div>)}
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Sticky Bottom Action Bar for Review Stage */}
      {stage === 'review' && <div className="absolute bottom-0 left-0 right-0 border-t border-geniac-border bg-geniac-panel/95 backdrop-blur-sm px-6 py-4 flex items-center justify-between z-10" data-id="element-306">
          <button onClick={onBack} className="px-4 py-2 text-sm font-medium text-geniac-muted border border-geniac-border rounded-lg hover:text-geniac-text hover:bg-geniac-hover transition-colors" data-id="element-307">
            Cancel
          </button>
          <button onClick={handleConfirm} className="px-6 py-2 text-sm font-semibold bg-geniac-accent text-geniac-bg rounded-lg hover:bg-geniac-accent/90 transition-colors shadow-lg shadow-geniac-accent/20" data-id="element-308">
            Confirm and Add to Case
          </button>
        </div>}

      {/* Success Toast */}
      <AnimatePresence data-id="element-309">
        {showSuccessToast && <motion.div initial={{
        opacity: 0,
        y: 50
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 50
      }} className="fixed bottom-24 left-1/2 -translate-x-1/2 bg-emerald-900/90 border border-emerald-800/50 text-emerald-100 px-4 py-3 rounded-lg shadow-xl flex items-center gap-3 z-50 backdrop-blur-md" data-id="element-310">
            <CheckCircle2Icon className="w-5 h-5 text-emerald-400" data-id="element-311" />
            <span className="text-sm font-medium" data-id="element-312">
              Successfully added {cards.length} responses to case.
            </span>
          </motion.div>}
      </AnimatePresence>
    </div>;
}